import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './sim-edit-panel.css';

/**
 * SimEditPanel — боковая панель редактирования SIM-карт
 * По макету Figma 26-04 wa Proxy Manager (node 436:73226)
 * 
 * Множественное редактирование: при выборе нескольких SIM-карт
 * отображается "Mixed" если значения различаются
 */

// ==================== SVG ICONS ====================

// Иконка закрытия (крестик)
const CloseIcon = () => (
  <svg
    className="sep__close-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Иконка dropdown (стрелка вниз)
const DropdownIcon = ({ isOpen = false }) => (
  <svg
    className={`sep__dropdown-icon ${isOpen ? 'sep__dropdown-icon--open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==================== SELECT COMPONENT ====================

const SelectField = ({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Выберите...',
  isMixed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const ref = useRef(null);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSearchText('');
      }
      return next;
    });
  };

  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange(optionValue);
    }
    setSearchText('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const matchedOption = options.find((o) => o.value === value);
  const displayValue = isMixed ? 'Mixed' : (matchedOption ? matchedOption.label : value || placeholder);
  const filteredOptions = options.filter((option) =>
    String(option.label || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="sep__input-group" ref={ref}>
      <div className="sep__input-label">
        <span className="sep__label-text">{label}</span>
      </div>
      <div className="sep__dropdown">
        <div
          className="sep__input-field"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={label}
        >
          <span className={`sep__input-value ${value && !isMixed ? 'sep__input-value--filled' : ''}`}>
            {displayValue}
          </span>
          <DropdownIcon isOpen={isOpen} />
        </div>
        {isOpen && (
          <div className="sep__dropdown-list" role="listbox">
            <div className="sep__dropdown-search">
              <input
                type="text"
                className="sep__dropdown-search-input"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Поиск..."
                aria-label={`Поиск: ${label}`}
                autoFocus
              />
            </div>
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`sep__dropdown-option ${value === option.value ? 'sep__dropdown-option--active' : ''}`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="sep__dropdown-empty">Ничего не найдено</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== TEXT INPUT COMPONENT ====================

const TextField = ({
  label,
  value,
  onChange,
  placeholder = '',
  isMixed = false,
  type = 'text',
}) => {
  const displayValue = isMixed ? 'Mixed' : (value || '');

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="sep__input-group">
      <div className="sep__input-label">
        <span className="sep__label-text">{label}</span>
      </div>
      <div className="sep__input-field">
        <input
          type={type}
          className={`sep__input-value ${value && !isMixed ? 'sep__input-value--filled' : ''}`}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder}
          aria-label={label}
        />
      </div>
    </div>
  );
};

// API базовый URL:
// - production: только same-origin (CSP connect-src 'self')
// - development: можно переопределить через REACT_APP_API_URL
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '');

// Функция для получения токена
const getAuthToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
};

// ==================== PERSONAL ACCOUNT SEARCH (EDIT) ====================

const SEP_PA_DROPDOWN_MARGIN = 4;
const SEP_PA_DROPDOWN_MAX_HEIGHT = 600;
const SEP_PA_DROPDOWN_VIEWPORT_PAD = 8;
const SEP_PA_CREATE_FORM_FLIP_THRESHOLD = 360;

const PersonalAccountEditField = ({
  value,
  onChange,
  isMixed = false,
  operatorOptions = [],
}) => {
  const [searchText, setSearchText] = useState('');
  const [allAccounts, setAllAccounts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: '', phone_number: '', password: '', operator: '' });
  const [paDropdownStyle, setPaDropdownStyle] = useState(null);
  const ref = useRef(null);
  const paTriggerRef = useRef(null);
  const paDropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const allLoadedRef = useRef(false);

  const updatePaDropdownPosition = useCallback(() => {
    if (!isOpen || !paTriggerRef.current) {
      setPaDropdownStyle(null);
      return;
    }
    const rect = paTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - SEP_PA_DROPDOWN_MARGIN - SEP_PA_DROPDOWN_VIEWPORT_PAD;
    const spaceAbove = rect.top - SEP_PA_DROPDOWN_MARGIN - SEP_PA_DROPDOWN_VIEWPORT_PAD;
    const openUp =
      showCreateForm &&
      spaceBelow < SEP_PA_CREATE_FORM_FLIP_THRESHOLD &&
      spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      SEP_PA_DROPDOWN_MAX_HEIGHT,
      Math.max(0, openUp ? spaceAbove : spaceBelow)
    );
    const vw = window.innerWidth;
    const pad = SEP_PA_DROPDOWN_VIEWPORT_PAD;
    let width = Math.min(rect.width, vw - 2 * pad);
    let left = rect.left;
    if (left + width > vw - pad) {
      left = vw - pad - width;
    }
    if (left < pad) {
      left = pad;
    }
    if (left + width > vw - pad) {
      width = Math.max(160, vw - left - pad);
    }
    const base = {
      position: 'fixed',
      left,
      width,
      maxWidth: vw - 2 * pad,
      boxSizing: 'border-box',
      maxHeight,
      zIndex: 1300,
      right: 'auto',
      marginTop: 0,
      overflowY: 'auto',
    };
    if (openUp) {
      setPaDropdownStyle({
        ...base,
        top: 'auto',
        bottom: window.innerHeight - rect.top + SEP_PA_DROPDOWN_MARGIN,
      });
    } else {
      setPaDropdownStyle({
        ...base,
        top: rect.bottom + SEP_PA_DROPDOWN_MARGIN,
        bottom: 'auto',
      });
    }
  }, [isOpen, showCreateForm]);

  useLayoutEffect(() => {
    const showList = isOpen && (searchResults.length > 0 || showCreateForm || isSearching);
    if (!showList) {
      setPaDropdownStyle(null);
      return;
    }
    updatePaDropdownPosition();
  }, [
    isOpen,
    searchResults,
    showCreateForm,
    isSearching,
    updatePaDropdownPosition,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleScrollOrResize = () => {
      updatePaDropdownPosition();
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePaDropdownPosition]);

  useEffect(() => {
    if (isMixed) {
      setSearchText('');
    } else if (value) {
      setSearchText(value.name || '');
    } else {
      setSearchText('');
    }
  }, [value, isMixed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current?.contains(e.target) || paDropdownRef.current?.contains(e.target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAllAccounts = useCallback(async () => {
    if (allLoadedRef.current && allAccounts.length > 0) return;
    setIsSearching(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/personal-accounts`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAllAccounts(data);
        allLoadedRef.current = true;
        if (!searchText) {
          setSearchResults(data);
          setShowCreateForm(false);
        }
      }
    } catch (err) {
      // ignore
    } finally {
      setIsSearching(false);
    }
  }, [searchText, allAccounts.length]);

  const handleSearch = useCallback(async (text) => {
    if (!text) {
      setSearchResults(allAccounts);
      setShowCreateForm(false);
      return;
    }
    const lower = text.toLowerCase();
    const filtered = allAccounts.filter(
      (a) =>
        a.name.toLowerCase().includes(lower) ||
        (a.phone_number && a.phone_number.includes(text))
    );
    setSearchResults(filtered);
    setShowCreateForm(filtered.length === 0 && text.length >= 2);
  }, [allAccounts]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    setIsOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(text), 150);
  };

  const handleFocus = () => {
    setIsOpen(true);
    loadAllAccounts();
    if (!searchText && allAccounts.length > 0) {
      setSearchResults(allAccounts);
    }
  };

  const handleSelectAccount = (account) => {
    setSearchText(account.name);
    setIsOpen(false);
    setShowCreateForm(false);
    if (onChange) onChange({ type: 'existing', id: account.id, name: account.name, operator: account.operator, phone_number: account.phone_number });
  };

  const handleNewAccountChange = (field, val) => {
    setNewAccount((prev) => ({ ...prev, [field]: val }));
  };

  const handleCreateNew = () => {
    const account = { ...newAccount, name: newAccount.name || searchText };
    if (onChange) onChange({ type: 'new', ...account });
    setIsOpen(false);
    setSearchText(account.name);
  };

  const handleClear = () => {
    setSearchText('');
    setSearchResults(allAccounts);
    setShowCreateForm(false);
    setIsOpen(false);
    if (onChange) onChange(null);
  };

  const handleSelectEmpty = () => {
    setSearchText('');
    setSearchResults(allAccounts);
    setShowCreateForm(false);
    setIsOpen(false);
    if (onChange) onChange(null);
  };

  const displayValue = isMixed ? 'Mixed' : searchText;

  return (
    <div className="sep__input-group sep__pa-field" ref={ref}>
      <div className="sep__input-label">
        <span className="sep__label-text">Личный кабинет</span>
      </div>
      <div className="sep__dropdown">
        <div className="sep__input-field" ref={paTriggerRef}>
          <input
            type="text"
            className={`sep__input-value ${displayValue ? 'sep__input-value--filled' : ''}`}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder="Поиск по имени или номеру..."
            aria-label="Личный кабинет"
            autoComplete="off"
          />
          {searchText && !isMixed && (
            <button type="button" className="sep__clear-btn" onClick={handleClear} aria-label="Очистить" tabIndex={0}>×</button>
          )}
        </div>

        {isOpen && (searchResults.length > 0 || showCreateForm || isSearching) && paDropdownStyle
          && ReactDOM.createPortal(
            <div
              ref={paDropdownRef}
              className="sep__dropdown-list sep__pa-dropdown sep__pa-dropdown--fixed"
              role="listbox"
              style={paDropdownStyle}
            >
              <div className="sep__pa-dropdown-scroll">
                {isSearching && <div className="sep__dropdown-loading">Загрузка...</div>}

                {!isSearching && (
                  <button
                    type="button"
                    className={`sep__dropdown-option sep__pa-option ${!value ? 'sep__dropdown-option--active' : ''}`}
                    onClick={handleSelectEmpty}
                    role="option"
                    aria-selected={!value}
                    tabIndex={0}
                  >
                    <span className="sep__pa-name">— Без ЛК —</span>
                  </button>
                )}

                {searchResults.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    className="sep__dropdown-option sep__pa-option"
                    onClick={() => handleSelectAccount(account)}
                    role="option"
                    aria-selected={value?.id === account.id}
                    tabIndex={0}
                  >
                    <span className="sep__pa-name">{account.name}</span>
                    <span className="sep__pa-meta">{account.operator} · {account.phone_number}</span>
                  </button>
                ))}
              </div>

              {showCreateForm && !isSearching && (
                <div className="sep__pa-create">
                  <div className="sep__pa-create-title">Создать новый ЛК</div>
                  <div className="sep__pa-create-field">
                    <label className="sep__pa-create-label">Имя / фамилия</label>
                    <div className="sep__input-field">
                      <input
                        type="text"
                        className={`sep__input-value ${(newAccount.name || searchText) ? 'sep__input-value--filled' : ''}`}
                        placeholder="Введите имя"
                        value={newAccount.name || searchText}
                        onChange={(e) => handleNewAccountChange('name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sep__pa-create-field">
                    <label className="sep__pa-create-label">Номер телефона ЛК</label>
                    <div className="sep__input-field">
                      <input
                        type="text"
                        className={`sep__input-value ${newAccount.phone_number ? 'sep__input-value--filled' : ''}`}
                        placeholder="Введите номер"
                        value={newAccount.phone_number}
                        onChange={(e) => handleNewAccountChange('phone_number', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sep__pa-create-field">
                    <label className="sep__pa-create-label">Пароль от ЛК</label>
                    <div className="sep__input-field">
                      <input
                        type="text"
                        className={`sep__input-value ${newAccount.password ? 'sep__input-value--filled' : ''}`}
                        placeholder="Введите пароль"
                        value={newAccount.password}
                        onChange={(e) => handleNewAccountChange('password', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sep__pa-create-field">
                    <label className="sep__pa-create-label">Оператор ЛК</label>
                    <div className="sep__input-field">
                      <select
                        className={`sep__input-value sep__pa-select ${newAccount.operator ? 'sep__input-value--filled' : ''}`}
                        value={newAccount.operator}
                        onChange={(e) => handleNewAccountChange('operator', e.target.value)}
                      >
                        <option value="">Выберите оператора</option>
                        {operatorOptions.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sep__btn sep__btn--save sep__pa-create-btn"
                    onClick={handleCreateNew}
                    disabled={!newAccount.phone_number || !newAccount.password || !newAccount.operator}
                    tabIndex={0}
                  >
                    Создать ЛК
                  </button>
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const SimEditPanel = ({
  isOpen = false,
  selectedItems = [],
  onClose,
  onSave,
  // Опции для дропдаунов (из API или статические)
  simStatusOptions = [
    { value: 'working', label: 'Работает' },
    { value: 'free', label: 'Свободен' },
    { value: 'reserve_msk', label: 'Резерв Мск' },
    { value: 'reserve_spb', label: 'Резерв СПб' },
    { value: 'check', label: 'Проверить' },
    { value: 'replace', label: 'Заменить' },
    { value: 'frozen', label: 'Заморожен' },
  ],
  operatorOptions = [
    { value: 'МТС', label: 'МТС' },
    { value: 'Мегафон', label: 'Мегафон' },
    { value: 'Билайн', label: 'Билайн' },
    { value: 'Теле2', label: 'Теле2' },
    { value: 'Yota', label: 'Yota' },
  ],
}) => {
  // Данные подключений с API
  const [connectionsData, setConnectionsData] = useState({
    connections: [],
    cities: [],
  });

  // Начальные значения форм (определяем Mixed если разные значения)
  const [formData, setFormData] = useState({
    simStatus: '',
    geo: '',
    server: '',
    modem: '',
    phoneNumber: '',
    operator: '',
    amount: '',
    lastPayment: '',
    nextPayment: '',
    freezeUntil: '',
    registeredBy: '',
    comment: '',
    imei: '',
    mac: '',
  });
  const [touchedFields, setTouchedFields] = useState({});

  // Выбранный / новый личный кабинет
  const [selectedPA, setSelectedPA] = useState(null);
  const [isPersonalAccountTouched, setIsPersonalAccountTouched] = useState(false);

  // Флаги Mixed для полей
  const [mixedFields, setMixedFields] = useState({
    simStatus: false,
    geo: false,
    server: false,
    modem: false,
    phoneNumber: false,
    operator: false,
    amount: false,
    lastPayment: false,
    nextPayment: false,
    freezeUntil: false,
    registeredBy: false,
    personalAccount: false,
    comment: false,
    imei: false,
    mac: false,
  });

  // Загрузка подключений при открытии
  useEffect(() => {
    if (isOpen) {
      const fetchConnections = async () => {
        try {
          const token = getAuthToken();
          const response = await fetch(`${API_BASE_URL}/api/sim-cards/connections`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (response.ok) {
            const data = await response.json();
            setConnectionsData(data);
          }
        } catch (err) {
          // Ошибка загрузки не критична
        }
      };
      fetchConnections();
    }
  }, [isOpen]);

  // Опции для ГЕО - уникальные города из подключений (+ явное «без города»)
  const geoOptions = [
    { value: '', label: '— Без города —' },
    ...connectionsData.cities.map((c) => ({
      value: c.value,
      label: c.label,
    })),
  ];

  // Опции для Сервер - фильтруем по выбранному ГЕО (+ явное «без сервера»)
  const serverOptions = useMemo(() => [
    { value: '', label: '— Без сервера —' },
    ...connectionsData.connections
      .filter((conn) => {
        if (!formData.geo) return true;
        return conn.city === formData.geo;
      })
      .map((conn) => ({
        value: conn.name,
        label: conn.name,
        city: conn.city,
      })),
  ], [connectionsData.connections, formData.geo]);

  // Опции для Модем — только «без модема», пока не выбран сервер; иначе модемы выбранного сервера
  const getModemNumberFromInternalIp = (ipAddress) => {
    if (!ipAddress || typeof ipAddress !== 'string') return null;
    const parts = ipAddress.trim().split('.');
    if (parts.length !== 4) return null;
    const octet = Number.parseInt(parts[2], 10);
    if (!Number.isFinite(octet)) return null;
    return octet;
  };

  const modemOptions = (() => {
    const withoutModem = { value: '', label: '— Без модема —' };
    if (!formData.server) {
      return [withoutModem];
    }
    const conns = connectionsData.connections.filter((c) => c.name === formData.server);
    const options = conns.flatMap((conn) =>
      (conn.modems || []).map((m) => {
        const modemNumber = getModemNumberFromInternalIp(m.modem_internal_ip);
        return {
          value: String(m.id),
          label: modemNumber != null
            ? `№ ${modemNumber} ${m.name || ''}`.trim()
            : (m.name || String(m.id)),
          modemNumber,
        };
      })
    );
    return [withoutModem, ...options];
  })();

  // Определяем начальные значения при открытии панели
  useEffect(() => {
    if (!isOpen || selectedItems.length === 0) {
      return;
    }

    // Проверяем каждое поле на одинаковость значений
    const checkMixed = (field) => {
      const values = selectedItems.map((item) => item[field]);
      const uniqueValues = [...new Set(values.filter((v) => v !== undefined && v !== null))];
      return uniqueValues.length > 1;
    };

    const getCommonValue = (field) => {
      const values = selectedItems.map((item) => item[field]);
      const uniqueValues = [...new Set(values.filter((v) => v !== undefined && v !== null))];
      return uniqueValues.length === 1 ? uniqueValues[0] : '';
    };

    const checkMixedRaw = (field) => {
      const values = selectedItems.map((item) => item._raw?.[field]);
      const uniqueValues = [...new Set(values.filter((v) => v !== undefined && v !== null && v !== ''))];
      return uniqueValues.length > 1;
    };

    const getCommonRawValue = (field) => {
      const values = selectedItems.map((item) => item._raw?.[field]);
      const uniqueValues = [...new Set(values.filter((v) => v !== undefined && v !== null && v !== ''))];
      return uniqueValues.length === 1 ? uniqueValues[0] : '';
    };

    setMixedFields({
      simStatus: checkMixed('simStatus'),
      geo: checkMixedRaw('geo'),
      server: checkMixed('server'),
      modem: checkMixedRaw('kraken_server_id'),
      phoneNumber: checkMixed('phoneNumber'),
      operator: checkMixed('operator'),
      amount: checkMixed('amount'),
      lastPayment: checkMixedRaw('last_payment_date'),
      nextPayment: checkMixedRaw('next_payment_date'),
      freezeUntil: checkMixedRaw('freeze_until'),
      registeredBy: checkMixedRaw('registered_by'),
      personalAccount: checkMixedRaw('personal_account_id'),
      comment: checkMixed('comment'),
      imei: checkMixed('imei'),
      mac: checkMixed('mac'),
    });

    const commonPA = (() => {
      const paIds = selectedItems.map((item) => item._raw?.personal_account_id).filter(Boolean);
      const uniqueIds = [...new Set(paIds)];
      if (uniqueIds.length === 1) {
        const pa = selectedItems[0]?._raw?.personal_account;
        if (pa) return { type: 'existing', id: pa.id, name: pa.name, operator: pa.operator, phone_number: pa.phone_number };
      }
      return null;
    })();
    setSelectedPA(commonPA);

    const rawModem = getCommonRawValue('kraken_server_id');
    const rawLastPayment = getCommonRawValue('last_payment_date');
    const rawNextPayment = getCommonRawValue('next_payment_date');
    const rawFreezeUntil = getCommonRawValue('freeze_until');
    const rawGeo = getCommonRawValue('geo');

    setFormData({
      simStatus: getCommonValue('simStatus'),
      geo: rawGeo || getCommonValue('geo'),
      server: getCommonValue('server'),
      modem: rawModem ? String(rawModem) : '',
      phoneNumber: getCommonValue('phoneNumber'),
      operator: getCommonValue('operator'),
      amount: getCommonValue('amount')?.toString() || '',
      lastPayment: rawLastPayment ? String(rawLastPayment).split('T')[0] : '',
      nextPayment: rawNextPayment ? String(rawNextPayment).split('T')[0] : '',
      freezeUntil: rawFreezeUntil ? String(rawFreezeUntil).split('T')[0] : '',
      registeredBy: getCommonRawValue('registered_by'),
      comment: getCommonValue('comment'),
      imei: getCommonValue('imei'),
      mac: getCommonValue('mac'),
    });
    setTouchedFields({});
    setIsPersonalAccountTouched(false);
  }, [isOpen, selectedItems]);

  // Обновление значения поля с логикой связи ГЕО → Сервер → Модем, lastPayment → nextPayment
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === 'server' && value) {
        const selectedConnection = connectionsData.connections.find(
          (conn) => conn.name === value
        );
        if (selectedConnection && selectedConnection.city) {
          newData.geo = selectedConnection.city;
        }
        newData.modem = '';
      }

      if (field === 'server' && !value) {
        newData.modem = '';
      }

      // Город меняем отдельно от сервера/модема: не сбрасываем server/modem в форме,
      // чтобы при сохранении не уезжали null в API и не затирались server_option/modem_option.

      // При смене последней даты → пересчёт следующей (+30 дней)
      if (field === 'lastPayment' && value) {
        const d = new Date(value);
        d.setDate(d.getDate() + 30);
        newData.nextPayment = d.toISOString().split('T')[0];
      }

      return newData;
    });
    setMixedFields((prev) => ({ ...prev, [field]: false }));
    setTouchedFields((prev) => {
      const next = { ...prev, [field]: true };
      if (field === 'server') {
        next.modem = true;
        next.geo = true;
      }
      return next;
    });
  }, [connectionsData.connections]);

  const handlePersonalAccountChange = useCallback((value) => {
    setSelectedPA(value);
    setIsPersonalAccountTouched(true);
    setMixedFields((prev) => ({ ...prev, personalAccount: false }));
  }, []);

  // Закрытие панели
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Сохранение данных
  const handleSave = useCallback(() => {
    if (onSave) {
      const changedData = {};
      Object.keys(formData).forEach((key) => {
        if (touchedFields[key]) {
          changedData[key] = formData[key];
        }
      });

      const optionLabelFor = (options, rawValue) => {
        if (rawValue === '' || rawValue == null) {
          return null;
        }
        const opt = options.find((o) => String(o.value) === String(rawValue));
        return opt && opt.label && !String(opt.label).startsWith('—') ? opt.label : null;
      };

      const stableModemOptionFor = (options, rawValue) => {
        if (rawValue === '' || rawValue == null) {
          return null;
        }
        const opt = options.find((o) => String(o.value) === String(rawValue));
        if (!opt || opt.modemNumber == null) {
          return null;
        }
        return `№ ${opt.modemNumber}`;
      };

      if (touchedFields.server) {
        changedData.server_option = optionLabelFor(serverOptions, formData.server);
      }
      if (touchedFields.modem) {
        changedData.modem_option = stableModemOptionFor(modemOptions, formData.modem);
      }

      if (isPersonalAccountTouched) {
        if (selectedPA && selectedPA.type === 'existing') {
          changedData.personalAccountId = selectedPA.id;
        } else if (selectedPA && selectedPA.type === 'new') {
          changedData.newPersonalAccount = {
            name: selectedPA.name,
            phone_number: selectedPA.phone_number,
            password: selectedPA.password,
            operator: selectedPA.operator,
          };
        } else {
          changedData.personalAccountId = null;
        }
      }

      onSave(changedData, selectedItems);
    }
    handleClose();
  }, [
    onSave,
    formData,
    touchedFields,
    selectedItems,
    selectedPA,
    isPersonalAccountTouched,
    handleClose,
    serverOptions,
    modemOptions,
  ]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Клик по оверлею — только настоящий: нажатие и отпускание на нём (просьба владельца 28.10)
  const overlayBackdrop = useBackdropClose(handleClose);

  if (!isOpen) {
    return null;
  }

  const panelContent = (
    <div
      className="sep-overlay"
      {...overlayBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sep-title"
    >
      <div className="sep">
        {/* Header */}
        <div className="sep__header">
          <h2 className="sep__title" id="sep-title">
            редактирование данных сим-карт
          </h2>
          <button
            type="button"
            className="sep__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="sep__content">
          {/* Selected count */}
          <p className="sep__selected">
            Выбрано: <span className="sep__selected-count">{selectedItems.length}</span>
          </p>

          {/* Строка 1: Статус SIM + Оператор */}
          <div className="sep__row">
            <SelectField
              label="Статус SIM"
              value={formData.simStatus}
              options={simStatusOptions}
              onChange={(val) => handleFieldChange('simStatus', val)}
              isMixed={mixedFields.simStatus}
            />
            <SelectField
              label="Оператор"
              value={formData.operator}
              options={operatorOptions}
              onChange={(val) => handleFieldChange('operator', val)}
              isMixed={mixedFields.operator}
            />
          </div>

          {/* Строка 2: Сервер + Модем */}
          <div className="sep__row">
            <SelectField
              label="Сервер"
              value={formData.server}
              options={serverOptions}
              onChange={(val) => handleFieldChange('server', val)}
              isMixed={mixedFields.server}
            />
            <SelectField
              label="Модем"
              value={formData.modem}
              options={modemOptions}
              onChange={(val) => handleFieldChange('modem', val)}
              placeholder={
                formData.server
                  ? 'Выберите модем'
                  : 'Сначала выберите сервер или «Без сервера»'
              }
              isMixed={mixedFields.modem}
            />
          </div>

          {/* Строка 3: ГЕО + Номер */}
          <div className="sep__row">
            <SelectField
              label="ГЕО"
              value={formData.geo}
              options={geoOptions}
              onChange={(val) => handleFieldChange('geo', val)}
              isMixed={mixedFields.geo}
            />
            <TextField
              label="Номер"
              value={formData.phoneNumber}
              onChange={(val) => handleFieldChange('phoneNumber', val)}
              placeholder="Введите номер телефона"
              isMixed={mixedFields.phoneNumber}
            />
          </div>

          {/* Строка 4: Сумма платежа + Последняя дата */}
          <div className="sep__row">
            <TextField
              label="Сумма платежа"
              value={formData.amount}
              onChange={(val) => handleFieldChange('amount', val)}
              type="number"
              isMixed={mixedFields.amount}
            />
            <TextField
              label="Последняя дата"
              value={formData.lastPayment}
              onChange={(val) => handleFieldChange('lastPayment', val)}
              type="date"
              isMixed={mixedFields.lastPayment}
            />
          </div>

          {/* Следующая дата + Заморожен до */}
          <div className="sep__row">
            <TextField
              label="Следующая дата"
              value={formData.nextPayment}
              onChange={(val) => handleFieldChange('nextPayment', val)}
              type="date"
              isMixed={mixedFields.nextPayment}
            />
            <TextField
              label="Заморожен до"
              value={formData.freezeUntil}
              onChange={(val) => handleFieldChange('freezeUntil', val)}
              type="date"
              isMixed={mixedFields.freezeUntil}
            />
          </div>

          {/* Личный кабинет */}
          <PersonalAccountEditField
            value={selectedPA}
            onChange={handlePersonalAccountChange}
            isMixed={mixedFields.personalAccount}
            operatorOptions={operatorOptions}
          />

          <TextField
            label="Оформлена"
            value={formData.registeredBy}
            onChange={(val) => handleFieldChange('registeredBy', val)}
            placeholder="Введите на кого оформлена"
            isMixed={mixedFields.registeredBy}
          />

          <div className="sep__row">
            <TextField
              label="IMEI"
              value={formData.imei}
              onChange={(val) => handleFieldChange('imei', val)}
              placeholder="IMEI модема"
              isMixed={mixedFields.imei}
            />
            <TextField
              label="MAC"
              value={formData.mac}
              onChange={(val) => handleFieldChange('mac', val)}
              placeholder="MAC-адрес модема"
              isMixed={mixedFields.mac}
            />
          </div>

          {/* Комментарий */}
          <TextField
            label="Комментарий"
            value={formData.comment}
            onChange={(val) => handleFieldChange('comment', val)}
            isMixed={mixedFields.comment}
          />
        </div>

        {/* Footer */}
        <div className="sep__footer">
          <button
            type="button"
            className="sep__btn sep__btn--cancel"
            onClick={handleClose}
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="sep__btn sep__btn--save"
            onClick={handleSave}
            tabIndex={0}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(panelContent, document.body);
};

export default SimEditPanel;
